/**
 * The Summary step's payment-intent scope: opens (or updates) the session's Stripe intent, priced
 * from the v2 cart, mounts Stripe's `Elements` with its client secret, and publishes that secret to
 * the card form below.
 *
 * The confirm cannot live in the place-order handler: `stripe.confirmPayment` needs the `elements`
 * instance, which only exists inside this scope. `StripeConfirmPublisher` sits inside it and puts
 * the confirm step on the store, where the place-order sequence picks it up.
 */
import { FC, Fragment, ReactNode, createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Stack, Typography } from '@mui/material'
import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js'

import { Path } from 'constants/router/paths'
import { GRAY_900 } from 'constants/styling/theme'
import { ProductKind } from 'constants/product'
import type { SavedStripePaymentMethod } from 'models/user'
import { logger } from 'utils/logging'
import { useAuth0 } from 'utils/auth'
import { MUICheckbox } from 'components/common/MUICheckBox'
import { ReceiptEmailInput } from 'components/common/ReceiptEmailInput'
import { SavedPaymentMethodsSelect } from 'components/common/SavedPaymentMethodsSelect'
import { SectionedBorderBox } from 'components/common/SectionedBorderBox'
import { SystemMessage } from 'components/common/SystemMessage'
import { StripePaymentContextProvider } from 'components/contexts/StripePaymentContext'
import { useSavedStripePaymentMethods } from 'components/contexts/SavedStripePaymentMethodsContext'

import { useRevampPaymentIntent } from 'dataQueries/purchaseFlowRevampPaymentIntent.query'

import { PfRetryAlert } from '../../components/PfRetryAlert.component'

import { PfStepPath, usePfPath, usePfRootPath } from '../../routing/pfBasePath'
import { usePfStore } from '../../store'
import { useEffectivePaymentMethod, useIsChargedAtCheckout } from '../billing/useEffectivePaymentMethod'

interface PaymentIntentScope {
  clientSecret?: string
  isStripePaymentMethod: boolean
  /** The intent could not be opened and there is none to fall back on. */
  isFailed?: boolean
  /** The failure is a placed order, which no re-send can price. */
  isOrderAlreadyPlaced?: boolean
  /** Re-sends the current body; absent when a re-send can only be refused again. */
  onRetry?: () => void
}

/** The intent's client secret, for the card form; outside the provider nothing is charged through Stripe. */
const PaymentIntentContext = createContext<PaymentIntentScope>({ isStripePaymentMethod: false })

/**
 * The card / SEPA input, and the confirm that goes with it.
 *
 *
 * PARITY with legacy's box, which this reproduces piece for piece:
 *   - saved cards / SEPA mandates first, because a returning client should not
 *     retype a card we already hold. They are NOT a Stripe Elements feature
 *     here: our own backend serves them (`GET .../stripe-payment-methods` via
 *     `SavedStripePaymentMethodsContextProvider`, mounted for the v2 tree by
 *     `LegacyStepShell`), and they confirm through a DIFFERENT Stripe call
 *     (`confirmCardPayment` / `confirmSepaDebitPayment` with the saved
 *     `payment_method`), not through `confirmPayment({ elements })`.
 *   - the receipt email, prefilled from the client's billing email and required
 *     — it is what Stripe sends the receipt to, and it is also passed as the
 *     payment method's billing email, which is why the element itself is told
 *     `fields.billingDetails.email: 'never'`.
 *   - "save this payment method", offered only for a NEW card.
 *
 * Rendered by the Summary page below the order review, not by the provider.
 *
 * It is split in two ON PURPOSE. The provider renders its children BOTH inside
 * the Elements scope (once a secret exists) and outside it (while the intent is
 * still opening, so the review is not blank), and `useStripe()` THROWS
 * outside Elements — hooks run before any early return, so a single component
 * that both gates and calls them takes the whole step down via the error
 * boundary. `StripePaymentSection` is the gate and calls no Stripe hook;
 * `StripePaymentBox` holds all of them and only ever renders inside Elements.
 *
 * Confirm choreography copied from legacy `PurchaseFlowPayment.module.tsx:99`:
 * `redirect: 'if_required'` keeps a 3DS challenge inline instead of taking a
 * top-level redirect, which is mandatory inside the cross-origin Propstack embed
 * where a top redirect throws the user out of the host CRM. The
 * `return_url` is used only when Stripe *does* have to redirect.
 */
export const StripePaymentSection: FC = () => {
  const { t } = useTranslation(['purchase_flow_revamp'])
  const { clientSecret, isStripePaymentMethod, isFailed, isOrderAlreadyPlaced, onRetry } = useContext(PaymentIntentContext)

  if (isStripePaymentMethod && isFailed) {
    return (
      <PfRetryAlert
        data-cy="pf-revamp-payment-intent-error"
        message={isOrderAlreadyPlaced
          ? t('purchase_flow_revamp:summary.order_already_placed')
          : t('purchase_flow_revamp:cart.proceed_requires_payment_intent_error')}
        onRetry={onRetry}
      />
    )
  }

  // The gate, and nothing else: no Stripe hook may run on this side of it.
  if (!isStripePaymentMethod || !clientSecret) return null

  return <StripePaymentBox clientSecret={clientSecret} />
}

/** Everything that needs the Elements scope. Rendered only when one exists. */
const StripePaymentBox: FC<{ clientSecret: string }> = ({ clientSecret }) => {
  // `saved_payment_methods` is where the save-for-later label lives — the same
  // namespace legacy's box reads it from, so the wording stays shared.
  const { t } = useTranslation(['purchase_flow_revamp', 'saved_payment_methods', 'order'])
  const paymentFailure = usePfStore((s) => s.paymentFailure)
  const stripe = useStripe()
  const elements = useElements()
  const { savedStripePaymentMethodsMap } = useSavedStripePaymentMethods()

  const paymentMethod = useEffectivePaymentMethod()
  const setStripeFormComplete = usePfStore((s) => s.setStripeFormComplete)
  const setStripeCardComplete = usePfStore((s) => s.setStripeCardComplete)
  const setStripeReceiptEmailValid = usePfStore((s) => s.setStripeReceiptEmailValid)
  const setStripeConfirmHandler = usePfStore((s) => s.setStripeConfirmHandler)

  /** `null` = pay with a new card (legacy's own sentinel for that state). */
  const [savedMethod, setSavedMethod] = useState<SavedStripePaymentMethod | null>(null)
  const [isElementComplete, setElementComplete] = useState(false)
  const [receiptEmail, setReceiptEmail] = useState('')
  const [isReceiptEmailValid, setReceiptEmailValid] = useState(false)
  const [saveMethod, setSaveMethod] = useState(false)

  const savedMethodKind = paymentMethod === 'sepa' ? ProductKind.SEPA_DIRECT_DEBIT : ProductKind.CARD_PAYMENT
  const savedMethods = savedStripePaymentMethodsMap[savedMethodKind] ?? []

  // Where Stripe sends the client back if it needs a redirect. Resolved from the base the flow is
  // mounted under, so the embed returns into the iframe and the main app to `PaymentStatus`.
  const pfPath = usePfPath()
  // The root, not the base: a session URL's base is never the bare mount point.
  const pfRoot = usePfRootPath()
  const returnPathRef = useRef<string>(Path.PAYMENT_STATUS)
  returnPathRef.current = pfRoot === Path.PURCHASE_FLOW_REVAMP ? Path.PAYMENT_STATUS : pfPath(PfStepPath.SUMMARY)

  // Everything the published handler reads, through refs: the handler is
  // published once and must not be reinstalled on every keystroke.
  const state = useRef({ clientSecret, savedMethod, receiptEmail, saveMethod, stripe, elements })
  state.current = { clientSecret, savedMethod, receiptEmail, saveMethod, stripe, elements }

  /** A saved method needs no card input; a new one needs a complete element.
   *  The receipt email is required either way, as it is in legacy. */
  const isCardComplete = savedMethod !== null || isElementComplete
  const isComplete = isCardComplete && isReceiptEmailValid

  useEffect(() => {
    setStripeFormComplete(isComplete)
    return () => setStripeFormComplete(false)
  }, [isComplete, setStripeFormComplete])

  useEffect(() => {
    setStripeCardComplete(isCardComplete)
    setStripeReceiptEmailValid(isReceiptEmailValid)
    return () => {
      setStripeCardComplete(null)
      setStripeReceiptEmailValid(null)
    }
  }, [isCardComplete, isReceiptEmailValid, setStripeCardComplete, setStripeReceiptEmailValid])

  const confirm = useCallback(async (orderId: number): Promise<{ clientSecret: string }> => {
    const { clientSecret: secret, savedMethod: saved, receiptEmail: email, saveMethod: save, stripe: stripeInstance, elements: elementsInstance } = state.current
    if (!stripeInstance || !secret) throw new Error('Stripe was not initialized')

    const params = new URLSearchParams({ dealId: orderId.toString() })
    // Stripe only redirects when it must (3DS), but when it does the target has to be inside the
    // iframe: `PaymentStatus` is a main-app route, so returning there would strand the broker in
    // the Backbone app inside the Propstack panel. The embed returns to its own Summary, where
    // `useStripeRedirectHandler` settles the order exactly as `PaymentStatus` would.
    const returnPath = returnPathRef.current
    const returnUrl = encodeURI(`${window.location.origin}${returnPath}?${params.toString()}`)
    const receipt = email || undefined

    // A saved method cannot go through `confirmPayment({ elements })` — the
    // element holds no card in that case. Legacy splits the same two ways.
    const { error } = saved
      ? await (saved.type === ProductKind.SEPA_DIRECT_DEBIT
        ? stripeInstance.confirmSepaDebitPayment(secret, { payment_method: saved.stripeId, receipt_email: receipt, return_url: returnUrl })
        : stripeInstance.confirmCardPayment(secret, { payment_method: saved.stripeId, receipt_email: receipt, return_url: returnUrl }))
      : await (async () => {
        if (!elementsInstance) throw new Error('Stripe was not initialized')
        return stripeInstance.confirmPayment({
          elements: elementsInstance,
          redirect: 'if_required',
          confirmParams: {
            return_url: returnUrl,
            receipt_email: receipt,
            save_payment_method: save,
            payment_method_data: { billing_details: { email: receipt } },
          },
        })
      })()

    if (error) {
      // Not the raw error: it can carry the payment intent's client secret and the billing details.
      logger.error('pf-revamp Stripe confirm failed', { type: error.type, code: error.code ?? null, declineCode: error.decline_code ?? null }, {
        context: { component: 'StripePaymentSection', action: 'confirmPayment' },
      })
      throw error
    }

    return { clientSecret: secret }
  }, [])

  const canConfirm = !!stripe

  useEffect(() => {
    if (!canConfirm) return
    setStripeConfirmHandler(confirm)
    return () => setStripeConfirmHandler(null)
  }, [canConfirm, confirm, setStripeConfirmHandler])

  const title = paymentMethod === 'sepa'
    ? t('purchase_flow_revamp:billing.payment_method.method_sepa')
    : t('purchase_flow_revamp:billing.payment_method.method_card')

  return (
    <SectionedBorderBox variant="flat" title={title} data-testid="pf-revamp-stripe-payment">
      <Stack gap={2}>

        {paymentFailure &&
          <SystemMessage
            variant="error"
            isRawContent
            message={paymentFailure.reason
              ? t('order:step_validation.payment_declined', paymentFailure)
              : t('order:step_validation.payment_not_completed', paymentFailure)}
          />
        }

        {savedMethods.length > 0 &&
          <SavedPaymentMethodsSelect
            isNewDesign
            paymentMethodType={savedMethodKind}
            savedPaymentMethods={savedMethods}
            selectedPaymentMethod={savedMethod}
            // `Nullable` also admits `undefined`; the section's own sentinel for
            // "pay with a new card" is `null`, so normalise here.
            onSelected={(method) => setSavedMethod(method ?? null)}
          />
        }

        <ReceiptEmailInput
          isRequired
          isNewDesign
          onValueChange={setReceiptEmail}
          onValidityChange={setReceiptEmailValid}
        />

        {savedMethod === null &&
          <Fragment>
            <PaymentElement
              onChange={(event) => setElementComplete(event.complete)}
              options={{
                terms: { card: 'never', sepaDebit: 'never' },
                // The receipt email above IS the billing email (passed on
                // confirm), so the element must not ask for it a second time.
                fields: { billingDetails: { email: 'never' } },
              }}
            />

            <MUICheckbox
              name="pf-revamp-save-payment-method"
              checked={saveMethod}
              onChange={(_, checked) => setSaveMethod(checked)}
              sx={{ marginRight: '1rem', padding: 0 }}
              label={
                <Typography color={GRAY_900} fontWeight={500} variant="text-md">
                  {t('saved_payment_methods:save')}
                </Typography>
              }
            />
          </Fragment>
        }

      </Stack>
    </SectionedBorderBox>
  )
}

/**
 * @component RevampPaymentIntentProvider
 * Opens the native intent for a Stripe cart and hands its client secret to Stripe's Elements and
 * the card form. Renders children untouched for an invoice cart, which needs no intent at all.
 */
export const RevampPaymentIntentProvider: FC<{ children?: ReactNode }> = ({ children }) => {
  const { roles } = useAuth0()
  const { syncIntent, clientSecret, isError, isOrderAlreadyPlaced, isPending, intentKey, amount } = useRevampPaymentIntent()
  const setIntentSyncing = usePfStore((s) => s.setIntentSyncing)
  const setIntentFailed = usePfStore((s) => s.setIntentFailed)
  const setIntentAmount = usePfStore((s) => s.setIntentAmount)
  // Closed as soon as the order is placed, before its payment is confirmed (see `forgetPlacedDraft`).
  const isOrderPlaced = usePfStore((s) => s.draft.isClosed)
  // The cart the placed order was priced with. A declined card may be retried for exactly that cart;
  // an edit after placement would confirm the old order's payment under a screen showing another cart.
  const [placedIntentKey, setPlacedIntentKey] = useState<string | null>(null)
  useEffect(() => {
    if (!isOrderPlaced) setPlacedIntentKey(null)
    else setPlacedIntentKey((current) => current ?? intentKey ?? null)
  }, [isOrderPlaced, intentKey])
  const isEditedAfterPlacement = isOrderPlaced && placedIntentKey !== null && intentKey !== placedIntentKey

  const isStripePaymentMethod = useIsChargedAtCheckout()

  /**
   * The `intentKey` we have already ATTEMPTED — not the one that succeeded.
   *
   * Keying on the body itself covers every input that moves the charge: lines,
   * add-ons, the fees the instructions step mirrors, the method and the address.
   * The legacy provider used a boolean `paymentIntentWasRequested` and reset it
   * only when the invoicing address changed, so a cart edited after the intent
   * opened confirmed a stale amount.
   *
   * "Attempted" rather than "synced" is load-bearing: a failure must NOT clear
   * it. It did once, and because the key is unchanged while the cart is
   * unchanged, a persistent failure re-fired the effect as fast as React could
   * re-render — 3916 requests against a backend answering 422 before anyone
   * noticed. One body gets one request; a different body gets another.
   *
   * A PUT keeps the same intent — and therefore the same client secret — so
   * Elements is not remounted and entered card details survive a resync.
   */
  const attemptedKeyRef = useRef<string | null>(null)

  /** One request for the current body. Reported, not rethrown: an unhandled rejection would blank
   *  the step, and `isError` is what the UI reads. */
  const attemptSync = useCallback((key: string) => {
    attemptedKeyRef.current = key
    syncIntent().catch((error) => {
      logger.error('pf-revamp could not sync the v2 payment intent', error, {
        context: { component: 'RevampPaymentIntentProvider', action: 'syncIntent' },
      })
    })
  }, [syncIntent])

  useEffect(() => {
    if (!isStripePaymentMethod) return
    // `role = ONLY_CLIENT` on both verbs of the endpoint. The BE already keeps
    // card/SEPA out of an admin's billing options, so this is a second gate: it
    // stops an admin cart from firing a request that can only 403.
    if (!roles.isClient) return
    if (!intentKey) return
    if (isPending) return
    // A placed order keeps the intent it was priced with; re-pricing it is refused anyway.
    if (isOrderPlaced) return
    // A failure keeps the key: automatic retries stay one per body, and the client retries by hand.
    if (attemptedKeyRef.current === intentKey) return

    attemptSync(intentKey)
  }, [attemptSync, intentKey, isOrderPlaced, isPending, isStripePaymentMethod, roles.isClient])

  // A placed order whose re-price was refused still holds the intent it was priced with; that one
  // is what the client confirms, so it is not a failure.
  const keepsPlacedIntent = isOrderAlreadyPlaced && !!clientSecret && !isEditedAfterPlacement
  const isFailed = (isError && !keepsPlacedIntent) || isEditedAfterPlacement

  const retry = useCallback(() => {
    if (isPending || !intentKey) return
    attemptSync(intentKey)
  }, [attemptSync, intentKey, isPending])

  // Mirrored into the store because the CTA that must not fire mid-resync lives
  // in the cart column, outside this tree.
  useEffect(() => {
    setIntentSyncing(isPending)
    return () => setIntentSyncing(false)
  }, [isPending, setIntentSyncing])

  // Same reason, and it is what lets the disabled CTA say "we could not load the
  // payment form" rather than blaming the client for an empty one. Legacy took
  // this from `paymentIntentMutation.isError` on the order-meta context, which no
  // v2 route ever wrote.
  useEffect(() => {
    setIntentFailed(isFailed)
    return () => setIntentFailed(false)
  }, [isFailed, setIntentFailed])

  // Same again: the cart column compares its displayed gross against what the intent will charge.
  // Only while the cart is charged at checkout: a cart that stops being one (a OneSystem cart dropping
  // its paid line) runs no resync, so the last intent's amount would sit next to a 0,00 gross.
  useEffect(() => {
    setIntentAmount(isStripePaymentMethod ? amount ?? null : null)
    return () => setIntentAmount(null)
  }, [amount, isStripePaymentMethod, setIntentAmount])

  if (!isStripePaymentMethod) {
    return (
      <PaymentIntentContext.Provider value={{ isStripePaymentMethod: false }}>
        <Fragment>{children}</Fragment>
      </PaymentIntentContext.Provider>
    )
  }

  return (
    <PaymentIntentContext.Provider
      value={{
        clientSecret,
        isStripePaymentMethod: true,
        isFailed,
        isOrderAlreadyPlaced: isOrderAlreadyPlaced || isEditedAfterPlacement,
        onRetry: isFailed && !isOrderAlreadyPlaced && !isEditedAfterPlacement ? retry : undefined,
      }}
    >
      {!roles.isClient && 'Admins are not allowed to place orders using stripe on behalf of clients!'}
      {!isFailed && !!clientSecret &&
        <StripePaymentContextProvider clientSecret={clientSecret}>
          {children}
        </StripePaymentContextProvider>
      }
      {/* No secret yet (or the open failed): render the review UI without an
          Elements scope rather than an empty step. The CTA cannot complete a
          card order without a confirm handler, so nothing can be half-placed. */}
      {(isFailed || !clientSecret) && <Fragment>{children}</Fragment>}
    </PaymentIntentContext.Provider>
  )
}
